import { useState } from 'react';
import { useSubjectLanguageVariants } from '@features/user';
import { ArrowForwardRounded, DescriptionOutlined, InfoOutlined } from '@mui/icons-material';
import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import type { ProviderPreviousNoteScope } from '../types';

interface EarlierNotesSettingProps {
  templateId: string;
  value?: ProviderPreviousNoteScope | null;
  canEdit: boolean;
  canFork: boolean;
  onChange?: (value: ProviderPreviousNoteScope) => void;
  defaultOpen?: boolean;
}

interface EarlierNotesChoice {
  value: ProviderPreviousNoteScope;
  label: string;
  description: string;
}

const CHOICES: EarlierNotesChoice[] = [
  {
    value: 'any_note',
    label: 'Latest note',
    description: 'Use the most recent completed note, even if it used a different template.',
  },
  {
    value: 'same_template',
    label: 'This template only',
    description: 'Keep notes made with this template building on one another.',
  },
  {
    value: 'none',
    label: 'Start fresh',
    description: 'Don’t carry wording forward from an earlier note.',
  },
];

const SUMMARY_BY_VALUE: Record<ProviderPreviousNoteScope, string> = {
  any_note: 'Latest note',
  same_template: 'This template only',
  none: 'Start fresh',
};

const NoteTrail = ({ value }: { value: ProviderPreviousNoteScope }) => {
  const isFresh = value === 'none';
  const isSameTemplate = value === 'same_template';

  return (
    <Stack
      direction="row"
      spacing={0.5}
      aria-hidden="true"
      data-testid="carry-forward-note-trail"
      sx={{ flexShrink: 0, alignItems: 'center' }}
    >
      <Paper
        variant="outlined"
        sx={{
          width: 28,
          height: 34,
          display: 'grid',
          placeItems: 'center',
          color: isFresh ? 'text.disabled' : isSameTemplate ? 'primary.main' : 'secondary.main',
          bgcolor: isFresh ? 'action.hover' : 'background.paper',
          borderColor: isFresh ? 'divider' : 'currentColor',
        }}
      >
        <DescriptionOutlined sx={{ fontSize: 17 }} />
      </Paper>
      <ArrowForwardRounded
        sx={{ fontSize: 17, color: isFresh ? 'text.disabled' : 'text.secondary' }}
      />
      <Paper
        variant="outlined"
        sx={{
          width: 32,
          height: 39,
          display: 'grid',
          placeItems: 'center',
          color: 'primary.main',
          bgcolor: 'primary.50',
          borderColor: 'primary.light',
          borderStyle: isFresh ? 'dashed' : 'solid',
        }}
      >
        <DescriptionOutlined sx={{ fontSize: 19 }} />
      </Paper>
    </Stack>
  );
};

export const EarlierNotesSetting = ({
  templateId,
  value,
  canEdit,
  canFork,
  onChange,
  defaultOpen = false,
}: EarlierNotesSettingProps) => {
  const effectiveValue = value ?? 'any_note';
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [draftValue, setDraftValue] = useState<ProviderPreviousNoteScope>(effectiveValue);
  const { singular: subjectLanguage } = useSubjectLanguageVariants();
  const { template: templateMetrics } = useEventTracking();
  const canEditOrCopy = canEdit || canFork;
  const summary = SUMMARY_BY_VALUE[effectiveValue];
  const controlLabel =
    canFork && !canEdit
      ? `Edit a copy of carry forward setting, ${summary}`
      : `Change carry forward setting, ${summary}`;

  const handleOpen = () => {
    setDraftValue(effectiveValue);
    setIsOpen(true);
    templateMetrics.earlierNotesOpened(templateId, effectiveValue);
  };

  const handleSelection = (nextValue: ProviderPreviousNoteScope) => {
    setDraftValue(nextValue);
    templateMetrics.earlierNotesSelected(templateId, nextValue);
  };

  const handleDone = () => {
    setIsOpen(false);
    if (draftValue !== effectiveValue) {
      onChange?.(draftValue);
    }
  };

  const summaryContent = (
    <Box sx={{ width: '100%', minWidth: 0 }}>
      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
        Carry forward
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
        What JotPsych reuses in the next note
      </Typography>
      <Stack direction="row" spacing={1} sx={{ alignItems: 'center', minHeight: 39 }}>
        <NoteTrail value={effectiveValue} />
        <Typography variant="body2" color="text.primary" sx={{ fontWeight: 700 }}>
          {summary}
        </Typography>
      </Stack>
    </Box>
  );

  return (
    <>
      {canEditOrCopy ? (
        <ButtonBase
          aria-label={controlLabel}
          onClick={handleOpen}
          sx={{
            width: '100%',
            minHeight: 72,
            p: 1,
            borderRadius: 1.5,
            border: '1px solid transparent',
            textAlign: 'left',
            justifyContent: 'stretch',
            transition: theme => theme.transitions.create(['background-color', 'border-color']),
            '&:hover': {
              bgcolor: 'action.hover',
              borderColor: 'divider',
            },
            '&.Mui-focusVisible': {
              outline: '2px solid',
              outlineColor: 'primary.main',
              outlineOffset: 2,
            },
          }}
        >
          {summaryContent}
        </ButtonBase>
      ) : (
        <Box
          aria-label={`Carry forward: ${summary}`}
          sx={{ width: '100%', minHeight: 72, p: 1, display: 'flex', alignItems: 'center' }}
        >
          {summaryContent}
        </Box>
      )}

      <Dialog
        open={isOpen}
        onClose={() => setIsOpen(false)}
        fullWidth
        maxWidth="sm"
        aria-labelledby="earlier-notes-title"
      >
        <DialogTitle id="earlier-notes-title">
          How should this template use earlier notes?
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Choose what JotPsych can carry forward when it writes a new note.
          </Typography>

          <FormControl fullWidth>
            <RadioGroup
              aria-label="Earlier notes choice"
              value={draftValue}
              onChange={event => handleSelection(event.target.value as ProviderPreviousNoteScope)}
              sx={{ gap: 1 }}
            >
              {CHOICES.map(choice => (
                <Paper
                  key={choice.value}
                  variant="outlined"
                  sx={{
                    borderColor: draftValue === choice.value ? 'primary.main' : 'divider',
                    bgcolor: draftValue === choice.value ? 'primary.50' : 'background.paper',
                    transition: theme =>
                      theme.transitions.create(['border-color', 'background-color']),
                    '&:hover': { borderColor: 'primary.light' },
                  }}
                >
                  <FormControlLabel
                    value={choice.value}
                    control={<Radio />}
                    sx={{
                      m: 0,
                      px: 1.25,
                      py: 1,
                      width: '100%',
                      alignItems: 'flex-start',
                      '& .MuiFormControlLabel-label': { flex: 1, minWidth: 0 },
                    }}
                    label={
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          gap: 1,
                          pr: 0.5,
                        }}
                      >
                        <Box>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>
                            {choice.label}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {choice.description}
                          </Typography>
                        </Box>
                        <NoteTrail value={choice.value} />
                      </Box>
                    }
                  />
                </Paper>
              ))}
            </RadioGroup>
          </FormControl>

          <Stack direction="row" spacing={1} sx={{ mt: 2, alignItems: 'flex-start' }}>
            <InfoOutlined sx={{ color: 'text.secondary', fontSize: 18, mt: 0.15 }} />
            <Typography variant="caption" color="text.secondary">
              Your {subjectLanguage}’s chart information stays available in every choice.
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDone}>Done</Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
